import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Record the road, not just the stops.
 *
 * A journey drawn from punch-in to each visit to punch-out is a straight line
 * between each pair, and nobody rides in straight lines - so the KM always read
 * short of the odometer. This drops a GPS breadcrumb as the rider moves, and the
 * backend measures along the trail instead.
 *
 * Two ways of collecting them, picked at runtime:
 *
 *   In the Android app, the BackgroundGeolocation plugin runs a foreground
 *   service, so the road is still recorded with the phone pocketed and the
 *   screen off - which is exactly when someone is riding. Android requires a
 *   visible notification for this, which is what `backgroundMessage` fills in.
 *
 *   In a browser there is no such thing, so it falls back to polling
 *   navigator.geolocation. That only runs while the tab is in the foreground;
 *   the gaps simply fall back to the straight-line distance, which is what the
 *   figure was before any of this.
 *
 * Points are buffered and posted in batches, so a tunnel or a dead signal
 * delays the upload rather than losing that stretch of road. The buffer lives
 * in sessionStorage and is only cleared once the server has taken it.
 */

const SAMPLE_MS = 20000;        // browser fallback: one fix per 20s
const FLUSH_MS = 120000;        // upload every 2 minutes
const FLUSH_AT_POINTS = 30;     // ...or sooner once a batch has built up
const MAX_ACCURACY_M = 100;     // a vaguer fix says little about where the rider was
const MOVE_FILTER_M = 25;       // matches the backend's jitter threshold
const BUFFER_KEY = 'keventer.trackBuffer';

const readBuffer = () => {
  try {
    return JSON.parse(sessionStorage.getItem(BUFFER_KEY) || '[]');
  } catch {
    return [];
  }
};

const writeBuffer = (points) => {
  try {
    sessionStorage.setItem(BUFFER_KEY, JSON.stringify(points));
  } catch {
    // A full or unavailable store must never break the app; the points in
    // memory still get posted, they just will not survive a reload.
  }
};

const backgroundPlugin = () => {
  // Reached through the runtime global rather than an import, so the web build
  // carries no dependency on a plugin that only exists inside the app.
  //
  // Capacitor hands back a PROXY for any plugin name asked of it, installed or
  // not, so `Plugins.BackgroundGeolocation` existing proves nothing - on an APK
  // built before the plugin was added, calling it returns undefined and the
  // `.then` on it took the whole app down after login. Hence three checks:
  // native at all, the plugin actually registered, and a callable addWatcher.
  const cap = typeof window !== 'undefined' ? window.Capacitor : undefined;
  if (!cap?.isNativePlatform?.()) return undefined;
  if (typeof cap.isPluginAvailable === 'function'
      && !cap.isPluginAvailable('BackgroundGeolocation')) return undefined;
  const plugin = cap.Plugins?.BackgroundGeolocation;
  return typeof plugin?.addWatcher === 'function' ? plugin : undefined;
};

export const useRouteTracker = (active) => {
  const { api } = useAuth();
  const buffer = useRef(readBuffer());
  const sending = useRef(false);

  useEffect(() => {
    if (!active) return undefined;

    let stopped = false;
    let watcherId = null;
    let sampler = null;

    const flush = async (force = false) => {
      if (sending.current || buffer.current.length === 0) return;
      if (!force && buffer.current.length < FLUSH_AT_POINTS) return;

      const batch = buffer.current;
      sending.current = true;
      try {
        await api.post('/attendance/track', { points: batch });
        // Only drop what was actually sent - anything captured while the
        // request was in flight stays queued.
        buffer.current = buffer.current.slice(batch.length);
        writeBuffer(buffer.current);
      } catch (err) {
        const status = err?.response?.status;
        if (status >= 400 && status < 500) {
          // The server will never take these: the day is closed, or was never
          // opened. Retrying forever would only grow the buffer and hammer the
          // API, so drop them and stop sampling.
          buffer.current = [];
          writeBuffer(buffer.current);
          stopped = true;
        }
        // Anything else - no signal, server restarting - keeps the points, and
        // the next flush retries them.
      } finally {
        sending.current = false;
      }
    };

    const add = ({ lat, lng, accuracy, speed, at }) => {
      if (stopped) return;
      if (accuracy != null && accuracy > MAX_ACCURACY_M) return;
      buffer.current = [...buffer.current, {
        lat, lng, accuracy: accuracy ?? null, speed: speed ?? null, at,
      }];
      writeBuffer(buffer.current);
      flush();
    };

    const plugin = backgroundPlugin();

    const startBrowserFallback = () => {
      if (stopped || sampler || !navigator.geolocation) return;
      const record = () => navigator.geolocation.getCurrentPosition(
        (position) => add({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          speed: position.coords.speed,
          at: new Date(position.timestamp || Date.now()).toISOString(),
        }),
        () => {
          // A refused or unavailable fix is not worth surfacing: the user is
          // mid-journey and the next sample may well succeed.
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
      );
      record();
      sampler = setInterval(record, SAMPLE_MS);
    };

    let watching = false;
    if (plugin) {
      try {
        // Promise.resolve, because an older shell can return something that is
        // not a promise at all. Tracking must never break the app around it.
        Promise.resolve(plugin.addWatcher(
        {
          backgroundTitle: 'Recording your route',
          backgroundMessage: 'Keventer Metro is measuring today’s travel for your TADA.',
          requestPermissions: true,
          stale: false,
          distanceFilter: MOVE_FILTER_M,
        },
        (position, error) => {
          if (error || !position) return;
          // A mock-location app would otherwise be able to invent a journey.
          if (position.simulated) return;
          add({
            lat: position.latitude,
            lng: position.longitude,
            accuracy: position.accuracy,
            speed: position.speed,
            at: new Date(position.time || Date.now()).toISOString(),
          });
        },
        )).then((id) => {
          if (stopped) {
            Promise.resolve(plugin.removeWatcher({ id })).catch(() => {});
          } else {
            watcherId = id;
          }
        }).catch(() => {
          // Permission refused, or the watcher never started: fall back to the
          // browser rather than recording nothing at all.
          startBrowserFallback();
        });
        watching = true;
      } catch {
        watching = false;
      }
    }

    if (!watching) {
      startBrowserFallback();
    }

    const flusher = setInterval(() => flush(true), FLUSH_MS);

    // In the browser, going to the background is when the timers stop, so send
    // what has been collected before that happens.
    const onHide = () => { if (document.visibilityState === 'hidden') flush(true); };
    document.addEventListener('visibilitychange', onHide);

    return () => {
      stopped = true;
      if (sampler) clearInterval(sampler);
      clearInterval(flusher);
      document.removeEventListener('visibilitychange', onHide);
      if (watcherId && plugin) {
        try {
          Promise.resolve(plugin.removeWatcher({ id: watcherId })).catch(() => {});
        } catch {
          // Nothing to clean up if the shell cannot remove it.
        }
      }
      flush(true);
    };
  }, [active, api]);
};

export default useRouteTracker;
